/**
 * Instagram Embed Extractor
 *
 * Extract and normalize Instagram permalinks from official embed HTML snippets.
 * This module intentionally has NO dependency on sns-links-utils to avoid circular imports.
 */

import { Patterns } from "../../constants/patterns";
import { instagramEmbedForm } from "./instagram-embed-form";

/**
 * Normalize Instagram permalink for embed.js.
 * Canonical form: https://www.instagram.com/(p|reel|tv)/<id>/ (no query/hash)
 */
export function normalizeInstagramPermalinkForEmbed(url: string): string | null {
    if (!url || typeof url !== "string") {
        return null;
    }

    const decoded = Patterns.decodeAmpersand(url.trim());
    if (!decoded) {
        return null;
    }

    try {
        const parsedUrl = new URL(decoded);
        if (!/instagram\.com$/i.test(parsedUrl.hostname) && !/\.instagram\.com$/i.test(parsedUrl.hostname)) {
            return null;
        }

        parsedUrl.protocol = "https:";
        parsedUrl.hostname = "www.instagram.com";
        parsedUrl.hash = "";
        parsedUrl.search = "";

        let out = parsedUrl.toString();
        if (!out.endsWith("/")) {
            out += "/";
        }

        return out;
    } catch {
        if (!/instagram\.com\//i.test(decoded)) {
            return null;
        }

        let out = decoded;
        out = out.replace(/#.*/, "").replace(/\?.*/, "");
        if (!out.endsWith("/")) {
            out += "/";
        }

        return out;
    }
}

/**
 * Extract Instagram permalink from official embed HTML.
 */
export function extractInstagramPermalinkFromOfficialEmbedHtml(officialEmbedHtml: string): string | null {
    if (!officialEmbedHtml || typeof officialEmbedHtml !== "string") {
        return null;
    }

    const raw = officialEmbedHtml.trim();
    if (!raw) {
        return null;
    }

    try {
        if (typeof DOMParser !== "undefined") {
            const doc = new DOMParser().parseFromString(raw, "text/html");
            const blockquote = instagramEmbedForm.getInstagramBlockquote(doc);
            if (blockquote) {
                const permalinkAttr = (blockquote.getAttribute("data-instgrm-permalink") || "").trim();
                if (permalinkAttr) {
                    return Patterns.decodeAmpersand(permalinkAttr);
                }

                const anchor = instagramEmbedForm.getInstagramAnchor(blockquote);
                if (anchor instanceof HTMLAnchorElement) {
                    const href = (anchor.getAttribute("href") || "").trim();
                    if (href) {
                        return Patterns.decodeAmpersand(href);
                    }
                }
            }

            const anyAnchor = instagramEmbedForm.getInstagramAnchor(doc);
            if (anyAnchor instanceof HTMLAnchorElement) {
                const href = (anyAnchor.getAttribute("href") || "").trim();
                if (href) {
                    return Patterns.decodeAmpersand(href);
                }
            }
        }
    } catch {
        // ignore DOM parsing failures
    }

    const permalinkMatch = raw.match(/data-instgrm-permalink\s*=\s*["']([^"']+)["']/i);
    if (permalinkMatch && permalinkMatch[1]) {
        return Patterns.decodeAmpersand(permalinkMatch[1].trim());
    }

    const hrefMatch = raw.match(/href\s*=\s*["']([^"']*instagram\.com[^"']*)["']/i);
    if (hrefMatch && hrefMatch[1]) {
        return Patterns.decodeAmpersand(hrefMatch[1].trim());
    }

    const urlMatch = raw.match(Patterns.INSTAGRAM_URL);
    if (urlMatch && urlMatch[0]) {
        return Patterns.decodeAmpersand(urlMatch[0].trim());
    }

    return null;
}
